import { Search, X, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface SearchableFilterProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  allLabel?: string;
}

export function SearchableFilter({ 
  options, 
  value, 
  onChange, 
  placeholder,
  allLabel = "All" 
}: SearchableFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search query
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSearchQuery('');
  };

  const getDisplayLabel = () => {
    if (value === 'all') return allLabel;
    return value;
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-[10px] pl-2 pr-5 py-1 bg-[#f6f6f6] text-[#262626] rounded-md border-0 cursor-pointer hover:bg-[#e7e7e7] transition-colors font-medium flex items-center gap-1 whitespace-nowrap"
      >
        {getDisplayLabel()}
        <ChevronDown className={`w-3 h-3 text-[#808080] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-[0px_4px_20px_-2px_rgba(0,0,0,0.15)] border border-[#e7e7e7] z-50 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-[#e7e7e7]">
            <div className="relative">
              <Search className="w-3 h-3 text-[#808080] absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${placeholder.toLowerCase()}...`}
                className="w-full pl-7 pr-6 py-1.5 text-[10px] bg-[#f6f6f6] text-[#262626] rounded-md border-0 focus:outline-none focus:ring-1 focus:ring-[#e30840]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#808080] hover:text-[#262626]"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {/* "All" option */}
            <button
              onClick={() => handleSelect('all')}
              className={`w-full text-left px-3 py-2 text-[10px] hover:bg-[#f6f6f6] transition-colors ${
                value === 'all' ? 'bg-[#f6f6f6] font-medium text-[#e30840]' : 'text-[#262626]'
              }`}
            >
              {allLabel}
            </button>

            {/* Filtered Options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-3 py-2 text-[10px] hover:bg-[#f6f6f6] transition-colors ${
                    value === option ? 'bg-[#f6f6f6] font-medium text-[#e30840]' : 'text-[#262626]'
                  }`}
                >
                  {option}
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-[10px] text-[#808080] text-center">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
